import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './TemplateEditor.html?raw';
import lang from 'dojo/_base/lang';

import _StatefulSection from '../../../moreCore/UI/_StatefulSection/_StatefulSection';
import _uiFrameMixin from '../../_loadingHelpers/_GlobalSelectEntityDialogsMixin';
import _GlobalSelectEntityDialogsMixin from '../../../moreCore/UI/Container/uiFrame';

import { TabContainer } from "../../../moreCore/_loadingHelpers/Tabbing";
import { Template } from "../ScreenplayTypes";

import GenericDialog from '../../../moreCore/UI/GenericDialog/GenericDialog';
import Edit from '../../../moreCore/UI/Edit/Edit';

class TemplateEditor extends compose(_StatefulSection, _uiFrameMixin, _GlobalSelectEntityDialogsMixin) {
    templateString: any = template;

    inputHtmlContent: MdsLax<InstanceType<typeof Edit>>;

    setup(tabContainer?: MdsLax<InstanceType<typeof TabContainer>>, entity?: Template, node?: HTMLElement) {
        let heading;

        if (entity) {
            this.internalName = "TemplateEditor_Editor_" + entity.ID;
            if (entity.is_new) {
                this.setData(entity);
                heading = this.translate("Screenplay_Heading_NewTemplateEditor");
            } else {
                heading = `${this.translate("Screenplay_Heading_TemplateEditor")}: ${entity.description}`;
                entity.is_new = false;
                this.setStartupState("ready");
                this._loadEntity(entity.ID);
            }

        } else {
            this.internalName = "TemplateEditor_Editor_New";
            let newEntity: any = {
                ID: "",
                $Displayname: "",
                Classname: "",
                ClassPrintname: "",
                is_new: true
            }
            this.setData(newEntity);
            heading = this.translate("Screenplay_Heading_NewTemplateEditor")
        }

        if (tabContainer) {
            this.show("tab", heading, tabContainer.name);
        } else if (node) {
            this.show("node", heading, tabContainer.name)
        }
        else {
            this.show("dlg", heading, tabContainer.name);
        }

        this.createDefaultButton("save", lang.hitch(this, this._onClickSave), false, "primary");
    }

    //LOAD
    private _loadEntity(entityId: string): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Template_LoadEntity",
            { ID: entityId },
            lang.hitch(this, this._onLoadEntityResponse),
            lang.hitch(this, this._onLoadEntityError));
    }

    private _onLoadEntityResponse(response: Template): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        response.is_new = false;
        this.setData(response);
    }

    private _onLoadEntityError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    //SAVE
    private _onClickSave(): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);

        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        let isNew = this.getAttributeValue("is_new");
        let changedData = this.getChangedData();
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Template_SaveEntity",
            { ID: entityId, Classname: classname, is_new: isNew, changedData: changedData },
            lang.hitch(this, this._onSaveResponse),
            lang.hitch(this, this._onSaveError),
            null,
            { skipXssCheck: true });
    }

    private _onSaveResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.emit("saved");
        this.showToast("Editor_Success_Save", "fas fa-check");
        this._loadEntity(response.entityId);
    }

    private _onSaveError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
        this.showToast("Editor_Error_Save", "fas fa-warning-triangle");
    }

    //ROLLBACK
    private _onClickRollback(e: MouseEvent): void {
        this.rollback();
    }

    //RELOAD
    private _onClickReload(e: MouseEvent): void {
        let entity = this.getAttributeValue("___root");
        this._loadEntity(entity.ID);
    }

    //DELETE
    private _onClickDelete(e: MouseEvent): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Template_DeleteEntity",
            { ID: entityId, Classname: classname },
            lang.hitch(this, this._onDeleteResponse),
            lang.hitch(this, this._onDeleteError));
    }

    private _onDeleteResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.close();
        this.showToast("Editor_Success_Delete", "fas fa-check");
    }

    private _onDeleteError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
        this.showToast("Editor_Error_Delete", "fas fa-warning-triangle");
    }


    //BUTTONS
    private _deactivateDataButtons(deactivate: boolean, deactivateAll?: boolean): void {
        this.requestContextButtonDisable("save", deactivate);
        this.requestContextButtonDisable("rollback", deactivate);
        if (deactivateAll) {
            this.requestContextButtonDisable("delete", deactivate);
            this.requestContextButtonDisable("reload", deactivate);
        }
    }

    //STATEFULSECTION
    protected onDirtyChange(newState: boolean) {
        this._deactivateDataButtons(!newState);
        this.setDirtyMarker(newState);
    }

    protected _onChanged(attribute: string, value: any) {
        switch (attribute) {
            case "html_content":
                let stringValue = JSON.stringify(value);
                console.log(value);
                stringValue = stringValue.replaceAll("\\n", "\\r\\n");
                stringValue = JSON.parse(stringValue);
                let encodedContent = encodeURI(stringValue);
                this.changedData[attribute] = encodedContent;
                break;
        }
    }

    private _onChangeHtmlContent(e) {
        console.log(JSON.stringify(e));
        
    }

    protected afterSetData(): void {
        super.afterSetData.apply(this, arguments as any);

        let isNew = this.getAttributeValue("is_new");
        if (!isNew) {
            this.createDefaultButton("delete", lang.hitch(this, this._onClickDelete), true, "secondary");
            this.createDefaultButton("rollback", lang.hitch(this, this._onClickRollback), false, "secondary");
            this.createDefaultButton("reload", lang.hitch(this, this._onClickReload), true, "primary");
            this.changeInternalName("templateEditor_" + this.getAttributeValue("ID"));
            this.setTitles(`${this.translate("Screenplay_Heading_Template")}: ${this.getAttributeValue("description")}`);

            let htmlContent = this.getAttributeValue("html_content");
            //this.inputHtmlContent.setValue(htmlContent, true);
        } else {

        }
        this._deactivateDataButtons(true);
    }

    //UIFRAMEMIXIN
    _onCloseRequest() {
        let hasChanged = this.isDirty() === true;

        if (hasChanged) {
            let dlgConfirm = new GenericDialog();
            dlgConfirm.show(this.translate("Screenplay_Template_AskSavePendingChanges"),
                this.translate("General_PendingChanges") + " - " + this.title,
                "",
                "",
                "yesnocancel",
                lang.hitch(this, this._confirmCallback));

            return false;

        } else {
            return true;
        }
    }

    private _confirmCallback(result) {
        if (result == -1) { // nein
            this.emit("notSaved");
            this.close();
        } else if (result == 0) { // cancel
            this.emit("canceled");
        }
        else {
            this._onClickSave();
            this.on("saved", lang.hitch(this, this.close));
        }
    }

    //WIDGETBASE
    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.SEDconfigCallfunction = "Webtool_API_Screenplay__Template_GetSEDConfig";
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(TemplateEditor);